/**
 * What a write to this entity *means*, stated before the write happens (Feature: terraform-provider,
 * task 9.1; Requirements 13.1, 13.2, 13.4; Design: §Design decision 6, §Components 10, Algorithm M).
 *
 * This replaces `AdoptionNotice`, which knew two origins. Origin is provenance, and provenance decides
 * the consequence of saving or deleting:
 *
 *  - **`manifest`** — the deployment manifest owns the entity and reconciles it on every
 *    `PUT /v1/deployments`; writing it through the API stamps it `api`, which removes it from that
 *    reconciliation for good. One-way, so it is stated before the write rather than reported after.
 *  - **`terraform`** — Terraform owns the entity *and keeps owning it*, which is the difference that
 *    matters. Adoption is permanent; a Terraform drift is not. Saving transfers management to the API,
 *    but the entity is still declared in HCL, so the change surfaces as drift on the next
 *    `terraform plan` and the next `apply` puts it back unless the configuration is updated to match
 *    (Req 13.1). Deleting is worse than it looks: Terraform re-creates the entity, with a **new id**
 *    (Req 13.2).
 *  - **`api`** — nothing to warn about; renders `null`.
 *
 * The `terraform` arm carries a checkbox the caller gates its submit on (Req 13.3). It is an
 * acknowledgement, **not a block** (§Design decision 6): the server cannot tell a dashboard `PATCH`
 * from any other editor's, Terraform's next `apply` bounds the damage, and the dashboard has to stay a
 * break-glass path for when Terraform CI is broken. A server-side lock is the requirements' Open
 * decision 4 and deliberately not built.
 *
 * The `manifest` arm renders **byte-identically to what `AdoptionNotice` rendered**, `data-testid`
 * included — Requirement 13.4 is that the shipped manifest behaviour does not move, and Requirement
 * 18.4 is that the pages' existing tests keep passing untouched. The new `terraform` arm gets its own
 * `provenance-notice` test id, so a test can tell the two apart without either one having to change.
 */

import type { EntityOrigin } from '@soroush/core';

/** Which kind of entity the notice is about, for the sentence's subject. */
export type ProvenanceEntity = 'check' | 'alert rule';

/** Whether the pending write saves the entity or deletes it — the consequence differs. */
export type ProvenanceAction = 'save' | 'delete';

export interface ProvenanceNoticeProps {
  /** The target entity's origin as read from the API (absent is treated as `manifest`, Req 6.8). */
  origin: EntityOrigin | undefined;
  entity: ProvenanceEntity;
  /** Defaults to `save` (the edit-form case). */
  action?: ProvenanceAction;
  /** Whether the user has ticked the acknowledgement. Only read for a `terraform` origin. */
  acknowledged?: boolean;
  /** Ticking or unticking the acknowledgement. Only called for a `terraform` origin. */
  onAcknowledge?: (acknowledged: boolean) => void;
}

/**
 * Whether writing this entity needs an explicit acknowledgement first (Req 13.3).
 *
 * Exported so the four write surfaces derive their `submitDisabled` from the same predicate the notice
 * renders from — a page cannot gate on a condition under which no checkbox appears, or leave a rendered
 * checkbox ungated. `manifest` deliberately does **not** require one: the manifest notice shipped
 * without a gate and Requirement 13.4 keeps it that way.
 */
export function requiresAcknowledgement(origin: EntityOrigin | undefined): boolean {
  return origin === 'terraform';
}

/**
 * Whether a write response reports an Ownership_Transfer **away from Terraform** (Req 13.5).
 *
 * The server sends `adopted: true` with `previousOrigin` on any transfer, so a manifest Adoption and a
 * Terraform hand-over are the same field pair with different contents. Only the Terraform case has a
 * consequence outside the platform — a `terraform plan` that will now show a diff — so only it is
 * confirmed differently. The two response envelopes (`UpdateCheckResponse`, `UpdateAlertRuleResponse`)
 * are structurally identical here, hence the structural parameter rather than two overloads.
 */
export function transferredFromTerraform(result: {
  adopted?: true;
  previousOrigin?: EntityOrigin;
}): boolean {
  return result.adopted === true && result.previousOrigin === 'terraform';
}

/**
 * The confirmation shown after a successful write that transferred management from Terraform to the API
 * (Req 13.5).
 *
 * Worth stating rather than leaving implicit, because the write's *visible* effect is the one the user
 * intended and its lasting effect is one they cannot see: the entity is now API-managed, the HCL that
 * declared it is out of date, and the next `terraform plan` will say so. Telling them here is what turns
 * a surprise in CI into a task they already know about.
 */
export function ProvenanceTransferNotice(): JSX.Element {
  return (
    <div role="status" className="adoption-notice" data-testid="transfer-notice">
      <p>
        Management transferred from Terraform to the API. The next <code>terraform plan</code> will show
        this change.
      </p>
    </div>
  );
}

export function ProvenanceNotice({
  origin,
  entity,
  action = 'save',
  acknowledged = false,
  onAcknowledge,
}: ProvenanceNoticeProps): JSX.Element | null {
  // An api-origin entity has no provenance consequence to state.
  if (origin === 'api') return null;

  if (origin === 'terraform') {
    return (
      <div role="note" className="adoption-notice" data-testid="provenance-notice">
        {action === 'save' ? (
          <>
            <p>
              This {entity} is managed by Terraform. Saving transfers management to the API.
            </p>
            <p className="muted">
              The change will appear as drift on the next <code>terraform plan</code>, and the next{' '}
              <code>terraform apply</code> will revert it unless the Terraform configuration is updated
              to match.
            </p>
          </>
        ) : (
          <>
            <p>
              This {entity} is managed by Terraform. Terraform still declares it, so the next{' '}
              <code>terraform apply</code> will plan to re-create it unless it is removed from the
              configuration.
            </p>
            <p className="muted">
              A re-created {entity} gets a new id, so its history and current state start over rather
              than carrying across.
            </p>
          </>
        )}
        {/* Implicit labelling, matching `SharingDisclosure`'s acknowledgement: no `id` to collide if a
            surface ever renders two notices, and still reachable by its accessible name. */}
        <label className="checkbox-row provenance-acknowledgement">
          <input
            type="checkbox"
            checked={acknowledged}
            data-testid="provenance-acknowledge"
            onChange={(event) => onAcknowledge?.(event.target.checked)}
          />
          <span>I understand this {entity} is managed by Terraform</span>
        </label>
      </div>
    );
  }

  // Absent origin means manifest at the read boundary, which is also the safe default here: warning
  // about a transfer that does not happen is recoverable, silently transferring ownership is not.
  return (
    <div role="note" className="adoption-notice" data-testid="adoption-notice">
      <p>
        This {entity} is managed by a deployment manifest. Modifying it through the API transfers
        management to the API (adoption); future manifest deploys will no longer update or delete it.
      </p>
      {action === 'save' ? (
        <p className="muted">Saving completes the transfer.</p>
      ) : (
        <p className="muted">
          The manifest still declares this {entity}, so the next manifest deploy will recreate it.
        </p>
      )}
    </div>
  );
}
